"use client";

import { useRef, useState } from "react";
import type { AppLocale } from "@/lib/i18n/constants";
import type { GoogleReviewPage, ReviewCarouselCopy } from "@/lib/reviews/types";
import styles from "./ReviewPlatformProofModule.module.css";

type Props = GoogleReviewPage & {
  readonly locale: AppLocale;
  readonly label: string;
  readonly copy: ReviewCarouselCopy;
  readonly sourceHref: string;
  readonly sourceLabel: string;
  readonly totalReviews?: number;
};

export default function GoogleReviewCarousel({
  reviews: initialReviews, nextCursor: initialCursor, locale, label, copy,
  sourceHref, sourceLabel, totalReviews,
}: Props) {
  const [reviews, setReviews] = useState(initialReviews);
  const [cursor, setCursor] = useState(initialCursor);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const inFlight = useRef(false);
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const rtl = locale === "he" || locale === "ar";
  const review = reviews[index];

  async function move(delta: number) {
    if (inFlight.current) return;
    setFailed(false);
    if (index + delta >= 0 && index + delta < reviews.length) {
      setIndex(index + delta);
      return;
    }
    if (delta < 0 || !cursor) return;
    inFlight.current = true;
    setLoading(true);
    try {
      const response = await fetch(`/api/reviews/google?${new URLSearchParams({ locale, cursor })}`, {
        signal: AbortSignal.timeout(10_000),
      });
      if (!response.ok) throw new Error("reviews_unavailable");
      const page: GoogleReviewPage = await response.json();
      if (!Array.isArray(page.reviews)) throw new Error("invalid_review_page");
      const known = new Set(reviews.map((item) => item.id));
      const additions = page.reviews.filter((item) => !known.has(item.id) && known.add(item.id));
      setReviews([...reviews, ...additions]);
      setCursor(page.nextCursor === cursor ? undefined : page.nextCursor);
      if (additions.length) setIndex(reviews.length);
    } catch {
      setFailed(true);
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }

  if (!review) return null;
  const nextDisabled = loading || (index === reviews.length - 1 && !cursor);
  // Once pagination ends, use the actual accessible count (Google's aggregate may include removed reviews).
  const count = cursor ? Math.max(totalReviews ?? reviews.length, reviews.length) : reviews.length;
  const number = (value: number) => new Intl.NumberFormat(locale).format(value);

  return (
    <section aria-label={label} className={styles.carousel} tabIndex={0}
      aria-busy={loading} data-review-carousel
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
        event.preventDefault();
        void move((event.key === "ArrowRight" ? 1 : -1) * (rtl ? -1 : 1));
      }}
      onTouchStart={(event) => {
        const touch = event.touches[0];
        touchStart.current = event.touches.length === 1 ? { x: touch.clientX, y: touch.clientY } : null;
      }}
      onTouchEnd={(event) => {
        const start = touchStart.current;
        touchStart.current = null;
        const touch = event.changedTouches[0];
        if (!start || !touch) return;
        const dx = touch.clientX - start.x;
        const dy = touch.clientY - start.y;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          void move((dx < 0 ? 1 : -1) * (rtl ? -1 : 1));
        }
      }}>
      <div className={styles.review} key={review.id} data-review-id={review.id}>
        <div className={styles.reviewMeta}>
          <span className={styles.stars} aria-label={review.ratingLabel}>
            <span aria-hidden="true">{"★".repeat(review.rating)}{"☆".repeat(5 - review.rating)}</span>
          </span>
          {review.publishedAt && review.publishedLabel
            ? <time dateTime={review.publishedAt}>{review.publishedLabel}</time> : null}
        </div>
        {review.comment ? <blockquote dir="auto">{review.comment}</blockquote> : <div className={styles.ratingOnly} aria-hidden="true">“</div>}
        <div className={styles.reviewer}>
          <span className={styles.avatar} aria-hidden="true">{Array.from(review.authorName)[0]}</span>
          <cite dir="auto">{review.authorName}</cite>
          <span className={styles.googleMark} aria-hidden="true">G</span>
        </div>
      </div>
      <div className={styles.controls}>
        <a href={review.href || sourceHref} target="_blank" rel="noopener noreferrer"
          data-analytics-event="review_proof_click" data-analytics-platform="google"
          data-analytics-location="google-review-carousel" className={styles.reviewLink}>
          {review.href ? copy.readReviewLabel : sourceLabel}<span aria-hidden="true"> ↗</span>
        </a>
        <div className={styles.navigation}>
          <span className={styles.counter} aria-live="polite" aria-atomic="true" dir="ltr">{number(index + 1)} / {number(count)}</span>
          <button type="button" aria-label={copy.previousReviewLabel} disabled={index === 0 || loading}
            onClick={() => void move(-1)} data-review-previous><span aria-hidden="true">{rtl ? "→" : "←"}</span></button>
          <button type="button" aria-label={copy.nextReviewLabel} disabled={nextDisabled}
            onClick={() => void move(1)} data-review-next><span aria-hidden="true">{rtl ? "←" : "→"}</span></button>
        </div>
      </div>
      {loading || failed ? <p role="status" className={styles.status}>{loading ? copy.loadingReviewsLabel : copy.reviewsUnavailableLabel}</p> : null}
    </section>
  );
}
